import type { Languages, LanguagesShort } from 'src/types/Languages';

export function toSnakeUpperCase(text: string): string {
  // Remove any existing spaces or special characters
  // Replace non-alphanumeric characters with a single underscore
  return (
    text
      .trim()
      .replace(/[^a-zA-Z0-9]/g, '_')
      // Convert to uppercase
      .toUpperCase()
      // Replace multiple consecutive underscores with a single underscore
      .replace(/_+/g, '_')
  );
}

export function toKebabCase(text: string): string {
  // Remove any existing spaces or special characters
  // Replace non-alphanumeric characters with a single underscore
  return (
    text
      .trim()
      .replace(/[^a-zA-Z0-9]/g, '-')
      // Convert to lowercase
      .toLowerCase()
      // Replace multiple consecutive underscores with a single underscore
      .replace(/-+/g, '-')
  );
}

export function toPascalCase(text: string): string {
  return text
    .trim()
    .replace(/[^a-zA-Z0-9]/g, '-')
    .replace(/-+/g, '-')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

export function startCase(text: string): string {
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function toAlphanumeric(str: string): string {
  return str.replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Characters students type in place of the ASCII apostrophe: iOS "smart
 * punctuation" (’ ‘), the Ukrainian keyboard apostrophe (ʼ), backtick/tilde,
 * acute accent, prime, fullwidth etc. They all look like a single quote.
 */
const APOSTROPHE_LOOKALIKES =
  /[\u2018\u2019\u201A\u201B\u02B9\u02BB\u02BC\u02BD\u02C8\u0060\u007E\u00B4\u2032\u2035\uFF07]/g;

export function normalizeApostrophes(str: string): string {
  return str.replace(APOSTROPHE_LOOKALIKES, "'");
}

export function removePunctuations(str: string): string {
  return normalizeApostrophes(str).replace(
    /[`~!@#$%^&*()_|+\-=?;:'",.<>{}/[\]]/g,
    '',
  );
}

export function removeExtraSpaces(str: string): string {
  return str.replace(/\s+/g, ' ').trim();
}

export function detectLanguage(text: string): LanguagesShort {
  const patterns = {
    uk: /[а-яА-ЯґҐєЄіІїЇ]/g,
    en: /[a-zA-Z]/g,
    he: /[\u0590-\u05FF]/g,
  };

  const counts: Record<string, number> = {};
  let maxCount = 0;
  let detectedLanguage: LanguagesShort = 'unknown';

  // Count characters for each language
  for (const [lang, regex] of Object.entries(patterns)) {
    const matches = text.match(regex);
    counts[lang] = matches ? matches.length : 0;

    if (counts[lang] > maxCount) {
      maxCount = counts[lang];
      detectedLanguage = lang as 'en' | 'uk' | 'he';
    }
  }

  // Return 'unknown' if no language characters found
  return maxCount > 0 ? detectedLanguage : 'unknown';
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escapes text for insertion into HTML markup (element content or a quoted attribute). */
export function escapeHtml(text: string): string {
  return String(text).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] as string);
}

/** The speech-recognition language for an answer written like `sample`. */
export function speechLangOf(sample: string | null | undefined): Languages {
  switch (detectLanguage(sample ?? '')) {
    case 'uk':
      return 'uk-UA';
    case 'he':
      return 'he-IL';
    default:
      return 'en-US';
  }
}
